import * as React from "react"
import {
  TextInput,
  SelectInput,
  ImageInput,
  ImageField,
  ReferenceInput,
  ArrayInput,
  SimpleFormIterator,
  BooleanInput,
  NumberInput,
  useDataProvider,
  TabbedForm,
  SaveButton,
  DeleteWithConfirmButton
} from "react-admin"
import { Box, Grid, Toolbar } from "@mui/material"
import { useWatch, useFormContext } from 'react-hook-form';
import { useEffect, useRef } from "react"

const LogoToolbar = ({formType}) => {
  return (
    <Toolbar>
      <Box display="flex" justifyContent="space-between" width="100%">
        {/* 儲存按鈕 (始終顯示) */}
        <SaveButton />
        {/*
          條件渲染刪除按鈕：
          1. 只在編輯模式顯示 (formType === "edit")
        */}
        { formType === "edit" && (
          <DeleteWithConfirmButton
            confirmTitle="確認刪除"
            confirmContent="您確定要刪除此LOGO嗎？"
          />
        )}
      </Box>
    </Toolbar>
  );
};

const validateForm = async (values) => {
  const errors = {}
  if (!values.name) {
    errors.name = "ra.validation.required"
  }
  if (!values.memberId) {
    errors.email = "ra.validation.required"
  }
  if (!values.logoImage?.rawFile && !values.logoImage?.[0]?.title) {
    errors.logoImage = "ra.validation.required"
  }
  /*
  try {
    if (values.isPrimary) {
      const { data } = await dataProvider.getList("logos", {
        filter: { memberId: values.memberId, isPrimary: true, excludeId: values.id },
        pagination: { page: 1, perPage: 1 },
        sort: { field: "createdAt", order: "ASC" },
      });
      if (data.length > 0) {
        errors.isPrimary = "resources.members.errors.default_exists"
      }
    }
  } catch (error) {
    console.error("驗證預設LOGO圖片錯誤:", error);
  }
  */

  return errors
}

const LogoForm = ({formType}) => {
  const dataProvider = useDataProvider()
  const prevFileRef = useRef(null);

  const CustomImageInput = () => {
  const { setValue, getValues } = useFormContext();
  const uploadedImage = useWatch({ name: 'logoImage' });
  const isInitialMount = useRef(true);

  useEffect(() => {
    // 跳過初次渲染
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // 只有當真正有新文件上傳時才處理
    if (uploadedImage?.rawFile && uploadedImage.rawFile !== prevFileRef.current) {
      const currentFile = uploadedImage.rawFile;
  
      // 檢查是否是有效的 File 對象
      const isValidFile = (
        currentFile &&
        typeof currentFile === 'object' &&
        currentFile instanceof File
      );

      if (!isValidFile) {
        // 不是有效文件時不做任何操作
        return;
      }

      prevFileRef.current = currentFile;

      const fetchPantoneColor = async () => {
        try {
          console.log(uploadedImage);
          const { data } = await dataProvider.getPantoneColor("logos/pantone-color", { 
            data: { logoImage: uploadedImage } 
          });

          if (data) {
            const cmykObjects = data.map(str => {
              const [c, m, y, k] = str.match(/\d+/g).map(Number);
              return { c, m, y, k };
            });
            
            const newColors = cmykObjects.map((item, index) => ({
              colorHex: "",
              c: item.c,
              m: item.m,
              y: item.y,
              k: item.k
            }));
            
            setValue("colors", newColors);
          }
        } catch (error) {
          console.error("取得 Pantone 顏色錯誤：", error);
        }
      };
    
      fetchPantoneColor();
    }
  }, [uploadedImage?.rawFile, setValue, uploadedImage]);

  return (
    <ImageInput 
      source="logoImage" 
      label="圖片(僅限PNG檔)"
      accept={{ 'image/png': ['.png'] }} 
    >
      <ImageField source="src" title="title" />
    </ImageInput>
  );
};

  return (
    <TabbedForm
      defaultValues={{
        memberId: "",
        name: "",
        description: "",
        isPrimary: false,
        reviewStatus: "pending",
        logoImage: [],
        colors: [
          {
            colorHex: "",
            c: "",
            m: "",
            y: "",
            k: "",
          },
        ]
      }}
      validate={validateForm}
      toolbar={<LogoToolbar formType={formType} />}
    >
      {/* 基本資料Tab */}
      <TabbedForm.Tab
        label="基本資料"
      >
        <Grid container width={{ xs: "90%", xl: 800 }} spacing={2}>
          <Grid item xs={12}>
            <CustomImageInput />
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextInput autoFocus source="name" label="名稱" isRequired />
          </Grid>
          <Grid item xs={12} sm={4}
            sx={{
              display: "flex",
              justifyContent: "right"
            }}
          >
            <BooleanInput label="主要圖片" source="isPrimary" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ReferenceInput source="memberId"
              reference="members"
              sort={{ field: 'createdAt', order: 'ASC' }}
              >
              <SelectInput label="所屬會員" optionText="name" isRequired/>
            </ReferenceInput>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SelectInput 
              source="reviewStatus" 
              label="審核狀態" 
              isRequired
              choices={[
                { id: 'pending', name: '待審核' },
                { id: 'approved', name: '審核通過' },
                { id: 'rejected', name: '審核未通過' }
              ]} 
            />
          </Grid>
          <Grid item xs={12}>
            <TextInput 
              source="description"
              label="描述" 
              multiline
              rows={4}
            />
          </Grid>
        </Grid>
      </TabbedForm.Tab>
      {/* 色號Tab */}
      <TabbedForm.Tab
        label="顏色色號"
        path="colors"
      >
        <Grid container width={{ xs: "90%", xl: 800 }} spacing={2}>
          <Grid item xs={12}>
            <ArrayInput source="colors" label="顏色色號">
              <SimpleFormIterator getItemLabel={(index) => `#${index + 1}`}>
                {/* HEX 欄位 */}
                <TextInput source="colorHex" label="HEX" sx={{ display: 'none' }} />
                {/* CMYK 欄位（排成同一列） */}
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", width: '100%' }}>
                  <NumberInput source="c" label="C" sx={{ width: '25%' }} />
                  <NumberInput source="m" label="M" sx={{ width: '25%' }} />
                  <NumberInput source="y" label="Y" sx={{ width: '25%' }} />
                  <NumberInput source="k" label="K" sx={{ width: '25%' }} />
                </Box>
              </SimpleFormIterator>
            </ArrayInput>
          </Grid>
        </Grid>
      </TabbedForm.Tab>
    </TabbedForm>
  )
}

export default LogoForm